/**
 * Promise-based wrappers around every client → server intent.
 *
 * - Each call waits (briefly) until the connection is up — and, for game
 *   intents, until this tab is (re)joined to its room — so an intent is never
 *   buffered onto a socket that has not re-claimed its seat yet.
 * - Each emit has a timeout. On timeout the SAME payload is re-sent: mutating
 *   intents carry an `actionId`, so the server replays its original ack if the
 *   first attempt had already been applied (safe, idempotent retries).
 * - Failures come back as `{ ok: false, error }` — the UI never sees a throw.
 */
import type {
  Activity,
  DictCheckResult,
  GameSettings,
  JoinResult,
  Placement,
  PlayResult,
  RoomPeek,
} from '@wordloom/engine';
import { newActionId } from '../lib/ids';
import { useRoomStore } from '../state/roomStore';
import { getBackend } from './backend';
import { OFFLINE_ERROR, TIMEOUT_ERROR, isTransportError, type Result } from './errors';

export interface RequestOptions {
  /** Per-attempt ack timeout. */
  timeoutMs?: number;
  /** Extra attempts after a timeout (same payload, same actionId). */
  retries?: number;
  /** Game intents need a joined seat; lobby intents only need a connection. */
  requireJoined?: boolean;
  /** How long to wait for the connection/seat before giving up as OFFLINE. */
  readyWaitMs?: number;
}

function isReady(requireJoined: boolean): boolean {
  const s = useRoomStore.getState();
  if (s.connection !== 'connected') return false;
  return !requireJoined || s.session?.joined === true;
}

/** Resolves true as soon as the transport is ready, false after `waitMs`. */
export function waitUntilReady(requireJoined: boolean, waitMs: number): Promise<boolean> {
  if (isReady(requireJoined)) return Promise.resolve(true);
  if (waitMs <= 0) return Promise.resolve(false);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(isReady(requireJoined));
    }, waitMs);
    const unsubscribe = useRoomStore.subscribe(() => {
      if (isReady(requireJoined)) {
        clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });
}

// `{}`: an ack with no payload beyond `ok`.
export async function request<T extends object = {}>(
  event: string,
  payload: unknown,
  opts: RequestOptions = {},
): Promise<Result<T>> {
  const { timeoutMs = 8000, retries = 1, requireJoined = true, readyWaitMs = 8000 } = opts;
  for (let attempt = 0; ; attempt++) {
    const ready = await waitUntilReady(requireJoined, readyWaitMs);
    if (!ready) return { ok: false, error: OFFLINE_ERROR };
    try {
      const ack = await getBackend().emit(event, payload, timeoutMs);
      if (ack && typeof ack === 'object' && 'ok' in ack) return ack as Result<T>;
      return { ok: false, error: { code: 'INTERNAL', message: 'Unexpected reply from the server.' } };
    } catch {
      if (attempt >= retries) return { ok: false, error: TIMEOUT_ERROR };
      // Loop: re-send the identical payload (identical actionId).
    }
  }
}

// ---------------------------------------------------------------------------
// actionId reuse across user-initiated retries
// ---------------------------------------------------------------------------

/**
 * Keeps ONE actionId per logical intent until the server answers it
 * definitively. If an attempt ended in a transport failure (timeout/offline),
 * pressing the button again with the same intent (same key) reuses the id, so
 * a play that did reach the server is never applied twice — the server just
 * replays its original ack.
 */
export class IntentIds {
  private pending = new Map<string, string>();

  /** The actionId for this intent key (reused until `settle`). */
  idFor(key: string): string {
    let id = this.pending.get(key);
    if (!id) {
      id = newActionId();
      this.pending.set(key, id);
    }
    return id;
  }

  /** Call with the result: a definitive answer retires the id; a transport failure keeps it. */
  settle(key: string, result: Result<object>): void {
    if (result.ok || !isTransportError(result.error)) this.pending.delete(key);
  }

  clear(): void {
    this.pending.clear();
  }
}

export const intentIds = new IntentIds();

/** Stable key for a play: same turn + same tiles on the same squares = same intent. */
export function playIntentKey(turn: number, placements: readonly Placement[]): string {
  const parts = placements
    .map((p) => `${p.tileId}@${p.row},${p.col}${p.letter ? `=${p.letter}` : ''}`)
    .sort();
  return `play:${turn}:${parts.join('|')}`;
}

async function withIntent<T extends object>(
  key: string,
  send: (actionId: string) => Promise<Result<T>>,
): Promise<Result<T>> {
  const actionId = intentIds.idFor(key);
  const result = await send(actionId);
  intentIds.settle(key, result);
  return result;
}

// ---------------------------------------------------------------------------
// Intents
// ---------------------------------------------------------------------------

export function createRoom(name: string, settings: Partial<GameSettings>): Promise<Result<JoinResult>> {
  return request<JoinResult>('room:create', { name, settings }, { requireJoined: false, retries: 0 });
}

export function joinRoom(code: string, name: string, token?: string): Promise<Result<JoinResult>> {
  const payload = token ? { code, name, token } : { code, name };
  return request<JoinResult>('room:join', payload, { requireJoined: false, retries: 1 });
}

/** The join form's "Ava invited you" line: read-only, one quick try (the form works without it). */
export function peekRoom(code: string): Promise<Result<RoomPeek>> {
  return request<RoomPeek>('room:peek', { code }, { requireJoined: false, retries: 0, timeoutMs: 4000 });
}

export function leaveRoom(): Promise<Result> {
  return request('room:leave', undefined, { requireJoined: false, retries: 0, readyWaitMs: 0, timeoutMs: 3000 });
}

export function play(turn: number, placements: Placement[]): Promise<Result<PlayResult>> {
  return withIntent(playIntentKey(turn, placements), (actionId) =>
    request<PlayResult>('game:play', { actionId, turn, placements }),
  );
}

export function exchange(turn: number, tileIds: string[]): Promise<Result> {
  const key = `exchange:${turn}:${[...tileIds].sort().join(',')}`;
  return withIntent(key, (actionId) => request('game:exchange', { actionId, turn, tileIds }));
}

export function pass(turn: number): Promise<Result> {
  return withIntent(`pass:${turn}`, (actionId) => request('game:pass', { actionId, turn }));
}

export function resign(gameNumber: number): Promise<Result> {
  return withIntent(`resign:${gameNumber}`, (actionId) => request('game:resign', { actionId }));
}

export function claimWin(gameNumber: number): Promise<Result> {
  return withIntent(`claim:${gameNumber}`, (actionId) => request('game:claimWin', { actionId }));
}

export function rematch(want: boolean): Promise<Result> {
  return request('game:rematch', { want });
}

export function dictCheck(words: string[]): Promise<Result<DictCheckResult>> {
  return request<DictCheckResult>('dict:check', { words }, { retries: 0, timeoutMs: 4000, readyWaitMs: 1500 });
}

export function sendActivity(activity: Activity): void {
  try {
    getBackend().sendActivity(activity);
  } catch {
    /* no backend yet — activity hints are best-effort */
  }
}
